import { useState, useEffect, useCallback } from "react";
import SortableTableHead, { SortConfig, nextSort, sortData } from "./SortableTableHead";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, RefreshCw, Building2, FlaskConical, ShieldCheck, Users, UserPlus, Trash2, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Pharmacy {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  fax: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  is_active: boolean;
  endotoxin_testing_required: boolean;
  coa_on_file: boolean;
  coa_expiry_date: string | null;
  current_batch_number: string | null;
  coa_document_url: string | null;
}

interface StaffMember {
  id: string;
  user_id: string;
  pharmacy_id: string;
  role: string;
  created_at: string;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
}

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  fax: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  endotoxin_testing_required: true,
  coa_on_file: false,
  coa_expiry_date: "",
  current_batch_number: "",
  coa_document_url: "",
};

const PharmacyManagement = () => {
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Pharmacy | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const [pharmSort, setPharmSort] = useState<SortConfig>({ key: "name", direction: "asc" });

  // Staff management state
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [staffPharmacy, setStaffPharmacy] = useState<Pharmacy | null>(null);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [addStaffEmail, setAddStaffEmail] = useState("");
  const [addStaffRole, setAddStaffRole] = useState("staff");
  const [addingStaff, setAddingStaff] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<StaffMember | null>(null);

  const fetchPharmacies = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("partner_pharmacies")
      .select("*")
      .order("name");
    if (data) setPharmacies(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPharmacies();
  }, [fetchPharmacies]);

  const fetchStaff = useCallback(async (pharmacyId: string) => {
    setStaffLoading(true);
    try {
      const { data: staffData } = await supabase
        .from("pharmacy_staff")
        .select("*")
        .eq("pharmacy_id", pharmacyId)
        .order("created_at");

      if (staffData && staffData.length > 0) {
        // Get user emails via the get_user_emails function
        const userIds = staffData.map((s) => s.user_id);
        const { data: emails } = await supabase.rpc("get_user_emails");

        // Get profiles for names
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", userIds);

        const emailMap: Record<string, string> = {};
        if (emails) {
          emails.forEach((e: any) => { emailMap[e.user_id] = e.email; });
        }
        const profileMap: Record<string, any> = {};
        if (profiles) {
          profiles.forEach((p: any) => { profileMap[p.user_id] = p; });
        }

        const enriched: StaffMember[] = staffData.map((s) => ({
          ...s,
          email: emailMap[s.user_id] || "unknown",
          first_name: profileMap[s.user_id]?.first_name || null,
          last_name: profileMap[s.user_id]?.last_name || null,
        }));
        setStaffMembers(enriched);
      } else {
        setStaffMembers([]);
      }
    } catch (err) {
      console.error("Error fetching staff:", err);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const openStaffDialog = (pharmacy: Pharmacy) => {
    setStaffPharmacy(pharmacy);
    setStaffDialogOpen(true);
    setAddStaffEmail("");
    setAddStaffRole("staff");
    fetchStaff(pharmacy.id);
  };

  const handleAddStaff = async () => {
    if (!addStaffEmail.trim() || !staffPharmacy) return;
    setAddingStaff(true);

    try {
      // Look up user by email
      const { data: userId, error: lookupError } = await supabase
        .rpc("get_user_id_by_email", { _email: addStaffEmail.trim().toLowerCase() });

      if (lookupError || !userId) {
        toast({ title: "User not found", description: "No account found with that email address.", variant: "destructive" });
        setAddingStaff(false);
        return;
      }

      // Add pharmacy_staff record
      const { error: staffError } = await supabase
        .from("pharmacy_staff")
        .insert({
          user_id: userId,
          pharmacy_id: staffPharmacy.id,
          role: addStaffRole,
        });

      if (staffError) {
        if (staffError.message.includes("duplicate") || staffError.code === "23505") {
          toast({ title: "Already assigned", description: "This user is already linked to this pharmacy.", variant: "destructive" });
        } else {
          toast({ title: "Error", description: staffError.message, variant: "destructive" });
        }
        setAddingStaff(false);
        return;
      }

      // Assign pharmacy role (ignore if already exists)
      const { error: roleError } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: "pharmacy" as any })
        .select();

      if (roleError && !roleError.message.includes("duplicate") && roleError.code !== "23505") {
        console.warn("Role assignment warning:", roleError.message);
      }

      toast({ title: "Staff added", description: `${addStaffEmail} has been linked to ${staffPharmacy.name} with the pharmacy role.` });
      setAddStaffEmail("");
      fetchStaff(staffPharmacy.id);
    } catch (err) {
      console.error("Error adding staff:", err);
      toast({ title: "Error", description: "An unexpected error occurred.", variant: "destructive" });
    } finally {
      setAddingStaff(false);
    }
  };

  const handleRemoveStaff = async (staff: StaffMember) => {
    setRemovingId(staff.id);
    try {
      // Remove from pharmacy_staff
      const { error } = await supabase
        .from("pharmacy_staff")
        .delete()
        .eq("id", staff.id);

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }

      // Check if user has any other pharmacy_staff records
      const { data: otherStaff } = await supabase
        .from("pharmacy_staff")
        .select("id")
        .eq("user_id", staff.user_id)
        .limit(1);

      // If no other pharmacy links, remove the pharmacy role
      if (!otherStaff || otherStaff.length === 0) {
        await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", staff.user_id)
          .eq("role", "pharmacy" as any);
      }

      toast({ title: "Staff removed", description: `${staff.email} has been unlinked from this pharmacy.` });
      if (staffPharmacy) fetchStaff(staffPharmacy.id);
    } catch (err) {
      console.error("Error removing staff:", err);
      toast({ title: "Error", description: "An unexpected error occurred.", variant: "destructive" });
    } finally {
      setRemovingId(null);
      setConfirmRemove(null);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: Pharmacy) => {
    setEditing(p);
    setForm({
      name: p.name,
      email: p.email,
      phone: p.phone || "",
      fax: p.fax || "",
      address: p.address || "",
      city: p.city || "",
      state: p.state || "",
      zip_code: p.zip_code || "",
      endotoxin_testing_required: p.endotoxin_testing_required,
      coa_on_file: p.coa_on_file,
      coa_expiry_date: p.coa_expiry_date || "",
      current_batch_number: p.current_batch_number || "",
      coa_document_url: p.coa_document_url || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: "Required fields", description: "Name and email are required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      fax: form.fax.trim() || null,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      zip_code: form.zip_code.trim() || null,
      endotoxin_testing_required: form.endotoxin_testing_required,
      coa_on_file: form.coa_on_file,
      coa_expiry_date: form.coa_expiry_date || null,
      current_batch_number: form.current_batch_number.trim() || null,
      coa_document_url: form.coa_document_url.trim() || null,
    };

    if (editing) {
      const { error } = await supabase
        .from("partner_pharmacies")
        .update(payload)
        .eq("id", editing.id);
      if (error) {
        toast({ title: "Update failed", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Pharmacy updated" });
      }
    } else {
      const { error } = await supabase
        .from("partner_pharmacies")
        .insert(payload);
      if (error) {
        toast({ title: "Add failed", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Pharmacy added" });
      }
    }

    setSaving(false);
    setDialogOpen(false);
    fetchPharmacies();
  };

  const toggleActive = async (p: Pharmacy) => {
    await supabase
      .from("partner_pharmacies")
      .update({ is_active: !p.is_active })
      .eq("id", p.id);
    fetchPharmacies();
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Building2 size={18} /> Partner Pharmacies
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchPharmacies}>
              <RefreshCw size={14} />
            </Button>
            <Button size="sm" className="gap-1.5" onClick={openAdd}>
              <Plus size={14} /> Add Pharmacy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : pharmacies.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 size={32} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">No partner pharmacies yet.</p>
              <Button size="sm" className="mt-4 gap-1.5" onClick={openAdd}>
                <Plus size={14} /> Add your first pharmacy
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead label="Name" sortKey="name" currentSort={pharmSort} onSort={(k) => setPharmSort(nextSort(pharmSort, k))} />
                  <SortableTableHead label="Email" sortKey="email" currentSort={pharmSort} onSort={(k) => setPharmSort(nextSort(pharmSort, k))} />
                  <TableHead>Phone</TableHead>
                  <SortableTableHead label="Location" sortKey="city" currentSort={pharmSort} onSort={(k) => setPharmSort(nextSort(pharmSort, k))} />
                  <SortableTableHead label="Status" sortKey="is_active" currentSort={pharmSort} onSort={(k) => setPharmSort(nextSort(pharmSort, k))} />
                  <TableHead>Compliance</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortData(pharmacies, pharmSort).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{p.email}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{p.phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={p.is_active ? "default" : "secondary"}
                        className="cursor-pointer"
                        onClick={() => toggleActive(p)}
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {p.endotoxin_testing_required && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <FlaskConical size={10} /> Endotoxin
                          </Badge>
                        )}
                        {p.coa_on_file && (
                          <Badge variant="outline" className="text-xs gap-1 border-primary/40 text-primary">
                            <ShieldCheck size={10} /> COA
                          </Badge>
                        )}
                        {p.current_batch_number && (
                          <Badge variant="secondary" className="text-xs">
                            Batch: {p.current_batch_number}
                          </Badge>
                        )}
                        {!p.endotoxin_testing_required && !p.coa_on_file && !p.current_batch_number && (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" onClick={() => openStaffDialog(p)} title="Manage Staff">
                        <Users size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} title="Edit Pharmacy">
                        <Pencil size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Add/Edit Pharmacy Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Pharmacy" : "Add Partner Pharmacy"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pharm-name">Name *</Label>
                  <Input id="pharm-name" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Acme Pharmacy" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pharm-email">Email *</Label>
                  <Input id="pharm-email" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder="rx@pharmacy.com" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pharm-phone">Phone</Label>
                  <Input id="pharm-phone" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="(555) 123-4567" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pharm-fax">Fax</Label>
                  <Input id="pharm-fax" value={form.fax} onChange={(e) => updateField("fax", e.target.value)} placeholder="(555) 123-4568" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pharm-address">Address</Label>
                <Input id="pharm-address" value={form.address} onChange={(e) => updateField("address", e.target.value)} placeholder="123 Main St" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pharm-city">City</Label>
                  <Input id="pharm-city" value={form.city} onChange={(e) => updateField("city", e.target.value)} placeholder="Austin" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pharm-state">State</Label>
                  <Input id="pharm-state" value={form.state} onChange={(e) => updateField("state", e.target.value)} placeholder="TX" maxLength={2} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pharm-zip">ZIP</Label>
                  <Input id="pharm-zip" value={form.zip_code} onChange={(e) => updateField("zip_code", e.target.value)} placeholder="78701" maxLength={10} />
                </div>
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-sm font-medium text-foreground">Quality & Compliance</p>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="pharm-endotoxin"
                    checked={form.endotoxin_testing_required}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, endotoxin_testing_required: !!checked }))}
                  />
                  <Label htmlFor="pharm-endotoxin" className="text-sm font-normal">Endotoxin testing required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="pharm-coa"
                    checked={form.coa_on_file}
                    onCheckedChange={(checked) => setForm((prev) => ({ ...prev, coa_on_file: !!checked }))}
                  />
                  <Label htmlFor="pharm-coa" className="text-sm font-normal">COA on file</Label>
                </div>
                {form.coa_on_file && (
                  <div className="space-y-3 pl-6">
                    <div className="space-y-2">
                      <Label htmlFor="pharm-coa-expiry">COA Expiry Date</Label>
                      <Input id="pharm-coa-expiry" type="date" value={form.coa_expiry_date} onChange={(e) => updateField("coa_expiry_date", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pharm-coa-url">COA Document URL</Label>
                      <Input id="pharm-coa-url" value={form.coa_document_url} onChange={(e) => updateField("coa_document_url", e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                )}
                <div className="space-y-2 pt-2">
                  <Label htmlFor="pharm-batch">Current Batch / Lot Number</Label>
                  <Input id="pharm-batch" value={form.current_batch_number} onChange={(e) => updateField("current_batch_number", e.target.value)} placeholder="e.g. LOT-2026-04-001" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : editing ? "Update" : "Add Pharmacy"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>

      {/* Staff Management Dialog */}
      <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={18} /> Manage Staff — {staffPharmacy?.name}
            </DialogTitle>
            <DialogDescription>
              Add or remove users who can access this pharmacy's dashboard. Users will automatically receive the pharmacy role.
            </DialogDescription>
          </DialogHeader>

          {/* Add staff form */}
          <div className="border rounded-lg p-4 bg-secondary/30 space-y-3">
            <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <UserPlus size={14} /> Add Staff Member
            </p>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="User email address"
                  value={addStaffEmail}
                  onChange={(e) => setAddStaffEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddStaff()}
                />
              </div>
              <Select value={addStaffRole} onValueChange={setAddStaffRole}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAddStaff} disabled={addingStaff || !addStaffEmail.trim()} className="gap-1.5">
                {addingStaff ? <RefreshCw size={14} className="animate-spin" /> : <UserPlus size={14} />}
                Add
              </Button>
            </div>
          </div>

          {/* Current staff list */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Current Staff</p>
            {staffLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : staffMembers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No staff members assigned yet.</p>
              </div>
            ) : (
              <div className="border rounded-lg divide-y divide-border">
                {staffMembers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {s.first_name && s.last_name
                          ? `${s.first_name} ${s.last_name}`
                          : s.email}
                      </p>
                      {s.first_name && s.last_name && (
                        <p className="text-xs text-muted-foreground">{s.email}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs capitalize">{s.role}</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setConfirmRemove(s)}
                        disabled={removingId === s.id}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Dialog */}
      <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove staff member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will unlink <strong>{confirmRemove?.email}</strong> from {staffPharmacy?.name}.
              {" "}If they have no other pharmacy assignments, their pharmacy role will also be revoked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmRemove && handleRemoveStaff(confirmRemove)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PharmacyManagement;
